import React, { useEffect, useRef, useState } from 'react';
import { Trophy, Ticket } from 'lucide-react';
import * as authApi from '../api/auth';

// Date bounds for the 18+ age gate. A convenience for whoever is filling the form in,
// never the control — a date input can be edited and the API can be called directly,
// so AgePolicy on the server is what actually decides.
const MINIMUM_AGE = 18;
const today = new Date();
const MAX_DATE = new Date(today.getFullYear() - MINIMUM_AGE, today.getMonth(), today.getDate())
  .toISOString().split('T')[0];
const MIN_DATE = '1900-01-01';

const EMPTY_FORM = {
  firstName: '',
  surname: '',
  email: '',
  password: '',
  confirmPassword: '',
  dateOfBirth: '2000-01-01'
};

/**
 * The code field is on this form rather than in front of it, and it is on the form for
 * everybody rather than only for whoever pressed the right button.
 *
 * It cannot be checked before an account exists - `JoinCodeController` requires a
 * session, deliberately, so an open lookup cannot be used to guess codes - so there is
 * nothing to validate here and no reason to make somebody type it on a screen of its
 * own. It is carried, not resolved: `JoinGameModal` does the resolving afterwards, and
 * it is the same modal, with the same errors, that an existing player uses.
 *
 * Shown always because most people signing up for this were sent a code by a mate. A
 * field that only appeared for whoever clicked "I've got a code" would be missing for
 * everyone who clicked the bigger button next to it with a code in their hand.
 */
function RegisterPage({ onAuthSuccess, onSwitchToLogin, onOpenPrivacy, focusJoinCode = false, initialJoinCode = '' }) {
  const [registerForm, setRegisterForm] = useState(EMPTY_FORM);
  // A join link fills this in (backlog item 89). Still editable: it is the player's form.
  const [joinCode, setJoinCode] = useState(initialJoinCode);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const joinCodeRef = useRef(null);

  // Arriving from the landing page's code entrance. The two buttons there go to the
  // same form, so this is what makes them different once you get here.
  useEffect(() => {
    if (focusJoinCode && joinCodeRef.current) {
      joinCodeRef.current.focus();
    }
  }, [focusJoinCode]);

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    if (registerForm.password !== registerForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (registerForm.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!registerForm.firstName || !registerForm.surname || !registerForm.email || !registerForm.dateOfBirth) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      await authApi.register({
        email: registerForm.email,
        firstName: registerForm.firstName,
        surname: registerForm.surname,
        password: registerForm.password,
        dateOfBirth: registerForm.dateOfBirth,
        enabled: true
      });
    } catch (error) {
      if (error.name === 'ApiError') {
        setError(error.text || 'Registration failed. Please try again.');
      } else {
        console.error('Registration error:', error);
        setError('An error occurred during registration. Please try again.');
      }
      setLoading(false);
      return;
    }

    // Creating the account sets no session, so sign in with what was just typed. Without this
    // the screen said "signed in" while the server did not know who it was talking to: the
    // first thing a new player did that needed an account was refused - a code from a mate
    // most of all - and a reload put them back on the landing page (backlog item 57).
    try {
      const data = await authApi.login({
        email: registerForm.email,
        password: registerForm.password
      });
      onAuthSuccess(data.player || data, joinCode.trim());
    } catch (error) {
      // The account exists, so "registration failed" would be untrue and a retry would be
      // refused as a duplicate. Say so, and point them at signing in.
      console.error('Sign-in after registration failed:', error);
      setError('Your account has been created. Please log in with your new email and password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container fade-in">
      <div className="auth-card">
        <Trophy className="auth-icon" />
        <h1>Create Account</h1>
        <p className="auth-subtitle">
          {initialJoinCode
            ? `You've been invited. Sign up and we'll put you straight in with code ${initialJoinCode}.`
            : 'Join the competition'}
        </p>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleRegister} className="auth-form">
          <div className="join-code-field">
            <label htmlFor="joinCode">
              <Ticket size={18} aria-hidden="true" />
              Got a code?
            </label>
            <input
              id="joinCode"
              ref={joinCodeRef}
              type="text"
              inputMode="text"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck="false"
              placeholder="e.g. 7FKQ2P"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              disabled={loading}
            />
            <small className="field-hint">
              Optional. Put the code a mate sent you here and we will take you straight
              into their game once you are signed up.
            </small>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First Name *</label>
              <input
                id="firstName"
                type="text"
                placeholder="John"
                value={registerForm.firstName}
                onChange={(e) => setRegisterForm({ ...registerForm, firstName: e.target.value })}
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="surname">Surname *</label>
              <input
                id="surname"
                type="text"
                placeholder="Doe"
                value={registerForm.surname}
                onChange={(e) => setRegisterForm({ ...registerForm, surname: e.target.value })}
                required
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="regEmail">Email *</label>
            <input
              id="regEmail"
              type="email"
              placeholder="john.doe@example.com"
              value={registerForm.email}
              onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="dateOfBirth">Date of Birth * (Must be {MINIMUM_AGE}+)</label>
            <input
              id="dateOfBirth"
              type="date"
              value={registerForm.dateOfBirth}
              onChange={(e) => setRegisterForm({ ...registerForm, dateOfBirth: e.target.value })}
              min={MIN_DATE}
              max={MAX_DATE}
              required
              disabled={loading}
            />
            <small className="field-hint">You must be at least {MINIMUM_AGE} to register</small>
          </div>

          <div className="form-group">
            <label htmlFor="regPassword">Password * (Min 8 characters)</label>
            <input
              id="regPassword"
              type="password"
              placeholder="Create a strong password"
              value={registerForm.password}
              onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
              required
              disabled={loading}
              minLength={8}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password *</label>
            <input
              id="confirmPassword"
              type="password"
              placeholder="Re-enter your password"
              value={registerForm.confirmPassword}
              onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
              required
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            className="primary-btn full-width"
            disabled={loading}
          >
            {loading ? 'Creating account...' : 'Create Account'}
          </button>

          {/* Beside the button that hands the details over, because this is the moment the
              question of what happens to them is worth answering (lmsbe backlog item 48). */}
          <p className="auth-switch">
            <button type="button" onClick={onOpenPrivacy}>
              How we look after your details
            </button>
          </p>

          <p className="auth-switch">
            Already have an account?{' '}
            <button type="button" onClick={onSwitchToLogin}>
              Log in here
            </button>
          </p>
        </form>
      </div>
    </div>
  );
}

export default RegisterPage;
